import type { DailyUsageRow } from "../blob/format";
import type { MergedSnapshot } from "../blob/merge";
import { costForUsage } from "../cost";
import type { PriceLookup } from "../pricing-table";
import { resolveLimit } from "../rollover";
import { TOOLS } from "../tools";
import type {
  DashboardData,
  LimitSnapshot,
  ResolvedLimit,
  ToolId,
  ToolStats,
  Totals,
} from "../types";
import { dayKey, startOfMonth } from "../windows";

/**
 * Compute the dashboard from decrypted blobs instead of from SQLite.
 *
 * The counterpart to `lib/usage.ts`, and deliberately the same output shape, so
 * the cards render identically whether the numbers came from a local database or
 * from two machines' published snapshots. Isomorphic — this runs in the browser,
 * so nothing here may reach for `node:*` or a database.
 *
 * Where the two readers necessarily differ, the difference is upstream rather
 * than here: a blob carries *aggregates*, so anything needing event-level
 * resolution is computed on the exporting machine and carried across in the
 * blob's per-tool state.
 */
export function dashboardFromSnapshot(
  snap: MergedSnapshot,
  lookup: PriceLookup,
  now: number = Date.now(),
): DashboardData {
  // The month boundary is a local-day key, matching how rows were bucketed on
  // the machine that exported them — comparing epoch timestamps here would slice
  // the boundary day differently than the exporter did.
  const monthStart = dayKey(startOfMonth(now));

  const byTool = new Map<ToolId, DailyUsageRow[]>();
  for (const row of snap.daily) {
    const list = byTool.get(row.tool);
    if (list) list.push(row);
    else byTool.set(row.tool, [row]);
  }

  const tools = {} as Record<ToolId, ToolStats>;
  for (const tool of TOOLS) {
    const state = snap.tools[tool.id];
    const rows = byTool.get(tool.id) ?? [];

    // Priced here, at read time, from whatever table this reader holds — never
    // from a cost baked in at export. That is what lets a price correction
    // repair historical figures without any machine re-publishing.
    let mtdCostUsd = 0;
    for (const r of rows) {
      if (r.day >= monthStart) mtdCostUsd += costForUsage(lookup, r.model, r);
    }

    tools[tool.id] = {
      // Carried from the exporting machine, where full event resolution exists.
      // Daily buckets cannot express a rolling 168-hour window without smearing
      // the boundary day, and cannot express a 5-hour one at all.
      last5hTokens: state?.last5hTokens ?? 0,
      last7dTokens: state?.last7dTokens ?? 0,
      mtdCostUsd,
      lastEventTs: state?.lastEventTs ?? null,
      limits: resolveLimits(state?.limits ?? [], now),
      // A tool no machine has published is `null` here, not an issue — and that
      // is the point. Locally this tool would carry "no Copilot account for this
      // card is signed in on this machine", which is a claim about *one*
      // machine; the exporter drops it precisely because the personal PC is in
      // no position to say anything about the work account. The reader
      // distinguishes "nothing reported" from "reported, and broken" by
      // `syncedAt === null`, not by inventing an issue here.
      collectIssue: state?.issue ?? null,
      tracksTokens: (state?.lastEventTs ?? null) !== null,
      // Manual entries are a local-database feature and are not published, so a
      // blob-fed dashboard has none to clear. Reporting `true` would offer a
      // button that could not do anything.
      hasManualEntries: false,
      syncedAt: state?.syncedAt ?? null,
    };
  }

  return {
    now,
    // The freshest publish across all machines. Per-tool `syncedAt` is the
    // number that actually matters here — one machine can be a day stale while
    // the other is seconds old — but this keeps the field meaningful for the
    // header rather than leaving it null.
    collectedAt: snap.machines.length > 0 ? snap.machines[0].exportedAt : null,
    tools,
    totals: computeTotals(tools),
  };
}

/**
 * Roll expired windows forward, exactly as the SQLite reader does.
 *
 * This matters more for blobs than it ever did locally: a work laptop that has
 * been shut since Friday publishes limit windows that lapsed days ago, and
 * showing those as current readings is precisely the failure the inference
 * exists to prevent.
 */
function resolveLimits(limits: LimitSnapshot[], now: number): ResolvedLimit[] {
  const bySlot = new Map<string, ResolvedLimit>();
  for (const s of limits) bySlot.set(`${s.window}:${s.label}`, resolveLimit(s, now));
  return [...bySlot.values()].sort(compareLimits);
}

function computeTotals(tools: Record<ToolId, ToolStats>): Totals {
  let mtdCostUsd = 0;
  let monthlySpendUsd = 0;
  let last7dTokens = 0;
  let activeTools = 0;
  let lastEventTs: number | null = null;

  for (const tool of TOOLS) {
    const s = tools[tool.id];
    mtdCostUsd += s.mtdCostUsd;
    monthlySpendUsd += tool.monthlyPriceUsd;
    last7dTokens += s.last7dTokens;
    if (s.mtdCostUsd > 0 || s.limits.some((l) => l.used > 0)) activeTools++;
    if (s.lastEventTs && (lastEventTs === null || s.lastEventTs > lastEventTs)) {
      lastEventTs = s.lastEventTs;
    }
  }

  return { mtdCostUsd, monthlySpendUsd, last7dTokens, activeTools, lastEventTs };
}

/** Order bars shortest-window first, then alphabetically by label. */
const WINDOW_ORDER = { "5h": 0, weekly: 1, monthly: 2 } as const;
function compareLimits(a: LimitSnapshot, b: LimitSnapshot): number {
  const w = WINDOW_ORDER[a.window] - WINDOW_ORDER[b.window];
  return w !== 0 ? w : a.label.localeCompare(b.label);
}
